import { AdminContainer, AlignedListContainer } from "../components/layouts";
import Time from "../lithic/Moment";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import { Chip } from "@material-ui/core";
import DetailSection from "../components/DetailSection";
import { GutterlessListItem as ListItem } from "../components/lists";
import ListItemText from "@material-ui/core/ListItemText";
import Link from "../components/Link";
import SimpleTable from "../components/SimpleTable";
import Typography from "@material-ui/core/Typography";
import api from "../api";
import { useParams, useHistory } from "react-router";
import React, { Suspense, useEffect, useState } from "react";

const AuditLogs = React.lazy(() => import("../components/AuditLogs"));

export default function CreditMonitoringDetailPage() {
  const { id } = useParams();
  const history = useHistory();
  const [creditMonitoring, setCreditMonitoring] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    setLoading(true);
    setError(null);
    api
      .getCreditMonitoring({ id })
      .then(api.pickData)
      .then((data) => {
        setCreditMonitoring(data);
        setLoading(false);
      })
      .catch((err) => {
        setError(err);
        setLoading(false);
      });
  }, [id]);

  if (loading)
    return (
      <AdminContainer variant="detail">
        <Typography>Loading...</Typography>
      </AdminContainer>
    );

  if (error) {
    const is404 = error.response?.status === 404;
    return (
      <AdminContainer variant="detail">
        <Typography variant="h6" color="error">
          {is404
            ? "Credit Monitoring not found"
            : "Error loading credit monitoring"}
        </Typography>
        <Box mt={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={() => history.goBack()}
          >
            Go Back
          </Button>
        </Box>
      </AdminContainer>
    );
  }

  if (!creditMonitoring) return null;

  return (
    <AdminContainer variant="detail">
      <Box mb={2}>
        <Button variant="outlined" onClick={() => history.goBack()}>
          ← Back
        </Button>
      </Box>

      <AlignedListContainer maxWidth="xl">
        <DetailSection title="Credit Monitoring Details">
          <ListItem>
            <ListItemText>
              <strong>ID:</strong> {creditMonitoring.id}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Status:</strong>{" "}
              {creditMonitoring.active ? (
                <Chip
                  size="small"
                  label="Active"
                  style={{ backgroundColor: "#4caf50", color: "#fff" }}
                />
              ) : (
                <Chip size="small" label="Inactive" />
              )}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Bureau:</strong> {creditMonitoring.bureau || "(not set)"}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Identity ID:</strong>{" "}
              <Link to={`/identities/${creditMonitoring.identityId}`}>
                {creditMonitoring.identityId}
              </Link>
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Enrollment ID:</strong>{" "}
              {creditMonitoring.enrollmentId != null ? (
                <Link
                  to={`/reporting-account-enrollments/${creditMonitoring.enrollmentId}`}
                >
                  {creditMonitoring.enrollmentId}
                </Link>
              ) : (
                "(none)"
              )}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>External ID:</strong>{" "}
              {creditMonitoring.externalId || "(not set)"}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Has Established Credit Score:</strong>{" "}
              {creditMonitoring.hasEstablishedCreditScore ? "Yes" : "No"}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Enrolled On:</strong>{" "}
              {creditMonitoring.enrolledOn || "(not set)"}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Last Pulled At:</strong>{" "}
              {creditMonitoring.lastPulledAt ? (
                <Time format="MMM DD, YYYY HH:mm:ss">
                  {creditMonitoring.lastPulledAt}
                </Time>
              ) : (
                "(not set)"
              )}
            </ListItemText>
          </ListItem>
          <ListItem>
            <ListItemText>
              <strong>Unenrolled At:</strong>{" "}
              {creditMonitoring.unenrolledAt ? (
                <Time format="MMM DD, YYYY HH:mm:ss">
                  {creditMonitoring.unenrolledAt}
                </Time>
              ) : (
                "(not set)"
              )}
            </ListItemText>
          </ListItem>
        </DetailSection>

        <DetailSection title="Credit Scores" mt={5}>
          {creditMonitoring.creditScores &&
          creditMonitoring.creditScores.length > 0 ? (
            <SimpleTable
              truncated
              tableProps={{ size: "small" }}
              headers={["ID", "Reported At", "Updated At"]}
              rows={creditMonitoring.creditScores}
              keyRowAttr="id"
              toCells={(row) => [
                row.id,
                row.reportedAtEpoch ? (
                  <Time key={1} format="MMM DD, YYYY HH:mm:ss">
                    {new Date(row.reportedAtEpoch).toISOString()}
                  </Time>
                ) : (
                  "(not set)"
                ),
                row.updatedAt ? (
                  <Time key={2} format="MMM DD, YYYY HH:mm:ss">
                    {row.updatedAt}
                  </Time>
                ) : (
                  "(not set)"
                ),
              ]}
            />
          ) : (
            <Typography variant="body2" color="textSecondary">
              No credit scores found.
            </Typography>
          )}
        </DetailSection>

        <DetailSection title="Audit Logs" mt={5}>
          <Suspense fallback={<Typography>Loading...</Typography>}>
            <AuditLogs auditableId={creditMonitoring.id} auditableType="CreditMonitoring" />
          </Suspense>
        </DetailSection>
      </AlignedListContainer>
    </AdminContainer>
  );
}
